import { apiClient } from '../../services/apiClient';
import { getApiErrorMessage } from '../../utils/apiErrors';

export interface CompanyRosterRow {
  id: string;
  user_id: string;
  role?: string;
  email?: string;
  username?: string;
  full_name?: string;
  is_active?: boolean;
}

export interface CompanyPlanRow {
  id: string;
  subscription_id?: string;
  company_id?: string;
  is_active?: boolean;
  subscription_type?: { subscription_name?: string; subscription_code?: string };
}

export function companyAdminError(err: unknown, fallback: string): string {
  return getApiErrorMessage(err, fallback);
}

export async function loadCompanyRoster(companyId: string): Promise<CompanyRosterRow[]> {
  const response = await apiClient.get<CompanyRosterRow[]>('/company-instances', {
    params: { company_id: companyId, active_only: false, limit: 500 },
  });
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows.filter((row) => String(row.user_id || '').trim());
}

export interface MemberProfileUpdate {
  username?: string;
  email?: string;
  full_name?: string;
  profile_data?: Record<string, unknown>;
  is_active?: boolean;
}

export async function updateMemberProfile(instanceId: string, body: MemberProfileUpdate): Promise<void> {
  await apiClient.put(`/company-instances/${instanceId}/member-profile`, body);
}

export async function updateCompanyMembershipRole(instanceId: string, role: string): Promise<void> {
  await apiClient.put(`/company-instances/${instanceId}`, { role });
}

export async function linkUserByEmail(
  callerInstanceId: string,
  email: string,
  role: string,
): Promise<void> {
  await apiClient.post(`/company-instances/${callerInstanceId}/invite-user`, {
    email: email.trim(),
    role,
    instance_name: 'Company member',
  });
}

export async function loadCompanyPlans(companyId: string): Promise<CompanyPlanRow[]> {
  const response = await apiClient.get<CompanyPlanRow[]>(
    `/subscriptions/companies/${companyId}/subscriptions`,
  );
  return Array.isArray(response.data) ? response.data : [];
}

export async function assignCompanySeat(companySubscriptionId: string, userId: string): Promise<void> {
  await apiClient.post(`/subscriptions/${companySubscriptionId}/assign-user`, { user_id: userId });
}

export async function loadCompanyTrees(companyId: string): Promise<Array<{ id?: string; name?: string }>> {
  const response = await apiClient.get<Array<{ id?: string; name?: string }>>('/artifacts/trees', {
    params: { company_id: companyId },
  });
  return Array.isArray(response.data) ? response.data : [];
}
